<template>
  <!-- Vai para o `body`: a tela do app é um contexto de empilhamento próprio, e dentro
       dele a barra de abas pintava por cima da folha — o rodapé dela ficava cortado.
       Fora, ela cobre a tela inteira, que é o que uma folha modal precisa fazer. -->
  <Teleport to="body">
    <!-- Como jogar hoje. Cada linha diz o que o modo é e o que ele rende — nome de modo
         sozinho ("Full-5") não diz nada a quem chegou agora. -->
    <div class="veil" @click.self="$emit('close')">
      <div class="sheet" role="dialog" aria-modal="true" aria-label="Escolher modo">
        <span class="handle" />
        <h2>Como você quer jogar?</h2>

        <div class="list">
          <!-- A chain do dia vem primeiro: é a que expira; depois o treino, que não pontua.
               Trilha e duelo não estão aqui: os dois têm aba própria, e a folha é só a
               escolha de qual chain jogar agora. -->
          <button
            v-for="mode in dailies"
            :key="mode.key"
            class="option"
            :class="{ on: mode.key === modelValue }"
            type="button"
            @click="$emit('choose', mode.key)"
          >
            <span class="brand brand-daily"><AppIcon name="calendar-days" size="lg" /></span>
            <span class="text">
              <span class="name">
                {{ mode.name }}
                <AppIcon v-if="mode.ranked" name="trophy" size="sm" class="ranked" />
                <i v-if="mode.key === modelValue" class="now">jogando agora</i>
              </span>
              <span class="about">{{ description(mode) }}</span>
            </span>
            <AppIcon name="chevron-right" size="lg" class="arrow" />
          </button>

          <button
            v-for="mode in trainings"
            :key="mode.key"
            class="option"
            :class="{ on: mode.key === modelValue }"
            type="button"
            @click="$emit('choose', mode.key)"
          >
            <span class="brand brand-training"><AppIcon name="refresh-cw" size="lg" /></span>
            <span class="text">
              <span class="name">
                {{ mode.name }}
                <i v-if="mode.key === modelValue" class="now">jogando agora</i>
              </span>
              <span class="about">{{ description(mode) }}</span>
            </span>
            <AppIcon name="chevron-right" size="lg" class="arrow" />
          </button>

          <!-- O fecho da última partida deixa de precisar sequestrar o estado base para
               ficar acessível: ele é uma entrada como as outras. -->
          <button v-if="hasResult" class="option" type="button" @click="$emit('result')">
            <span class="brand brand-result"><AppIcon name="eye" size="lg" /></span>
            <span class="text">
              <span class="name">Ver o resultado da última partida</span>
              <span class="about">O fecho da chain que você acabou de jogar</span>
            </span>
            <AppIcon name="chevron-right" size="lg" class="arrow" />
          </button>
        </div>

        <button class="btn btn-outline" type="button" @click="$emit('close')">fechar</button>
      </div>
    </div>
  </Teleport>
</template>

<script>
import AppIcon from '../ui/AppIcon.vue';

export default {
  name: 'ModeSheet',
  components: { AppIcon },
  props: {
    /** Os modos que o servidor ofereceu — sem lista fixa no código. */
    modes: { type: Array, default: () => [] },
    modelValue: { type: String, default: '' },
    /** Existe fecho guardado para reler. */
    hasResult: { type: Boolean, default: false },
  },
  emits: ['choose', 'result', 'close'],
  computed: {
    dailies() {
      return this.modes.filter((m) => m.is_daily);
    },
    trainings() {
      return this.modes.filter((m) => !m.is_daily);
    },
  },
  methods: {
    description(mode) {
      const shape = mode.min_word_length === mode.max_word_length
        ? `Palavras de ${mode.min_word_length} letras.`
        : 'Palavras de tamanhos variados.';
      if (mode.is_daily && mode.ranked) return `${shape} A mesma para todo mundo, e pontua o ranking`;
      if (mode.is_daily) return `${shape} A mesma para todo mundo, uma por dia`;
      return `${shape} Quantas partidas quiser, sem pontuar`;
    },
  },
};
</script>

<style scoped>
.veil {
  position: fixed;
  inset: 0;
  z-index: 28;
  display: flex;
  align-items: flex-end;
  background: var(--modal-veil);
  animation: appear 0.2s ease-out;
}

@keyframes appear { from { opacity: 0; } to { opacity: 1; } }

.sheet {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 90%;
  overflow-y: auto;
  padding: 12px 18px calc(18px + env(safe-area-inset-bottom));
  border-radius: 28px 28px 0 0;
  background: var(--raised);
  border-top: 1px solid var(--line-2);
  animation: rise 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes rise { from { transform: translateY(18px); opacity: 0; } to { transform: none; opacity: 1; } }

.handle {
  width: 44px;
  height: 4px;
  border-radius: var(--r-pill);
  background: var(--line-2);
  align-self: center;
}

.sheet h2 { padding: 4px 0; font-size: 22px; }

.list { display: flex; flex-direction: column; gap: 8px; }

.option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: var(--touch);
  padding: 12px 13px;
  border-radius: 15px;
  background: var(--veil-1);
  border: 1px solid var(--line);
  color: inherit;
  text-align: left;
  text-decoration: none;
}

.option:hover { border-color: var(--line-2); }
.option.on { border-color: var(--active-line); background: var(--active-veil); }

/* Cada modo tem o seu par de cores da marca: é o que faz a lista ser lida de relance. */
.brand {
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: 13px;
  display: grid;
  place-items: center;
  color: var(--text);
}

.brand-trail { background: linear-gradient(135deg, var(--purple), var(--magenta)); }
.brand-result { background: var(--veil-3); color: var(--text); }
.brand-daily { background: linear-gradient(135deg, var(--link-blue), var(--violet)); }
.brand-training { background: linear-gradient(135deg, var(--active), var(--violet)); }

.text { flex: 1; min-width: 0; }

.name {
  display: flex;
  align-items: center;
  gap: 8px;
  font: 700 15px/1 var(--f-title);
  color: var(--text);
}

/* "Jogando agora" é estado, não etiqueta de tipo: ele usa a cor do ativo (diretiva 02). */
.now {
  padding: 3px 8px;
  border-radius: var(--r-pill);
  border: 1px solid var(--active-line);
  background: var(--active-veil);
  color: var(--active);
  font: 700 9.5px/1 var(--f-label);
  font-style: normal;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.ranked { color: var(--amber); }

.about {
  display: block;
  margin-top: 4px;
  font: 600 12px/1.35 var(--f-body);
  color: var(--text-3);
}

.arrow { color: var(--text-3); }
</style>
